import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { LuX } from "react-icons/lu";
import { useIsCompactView } from "../core_stores/useIsCompactViewStore";

interface FreeTokensModalContextType {
  show: () => void;
}

const FreeTokensModalContext = createContext<
  FreeTokensModalContextType | undefined
>(undefined);

export function useFreeTokensModal() {
  const ctx = useContext(FreeTokensModalContext);
  if (!ctx)
    throw new Error(
      "useFreeTokensModal must be used within a FreeTokensModalProvider"
    );
  return ctx;
}

export function FreeTokensModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const isCompactView = useIsCompactView();

  useEffect(() => {
    // Prevent background scroll when modal is open
    if (isCompactView) {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
    }, 300);
  };

  const show = useCallback(() => {
    setIsClosing(false);
    setIsOpen(true);
  }, []);

  return (
    <FreeTokensModalContext.Provider value={{ show }}>
      {children}
      {isOpen && (
        <div
          className={`${
            isClosing ? "animate-fade-out" : "animate-fade-in"
          } fixed inset-0
                  flex items-center justify-center
                  bg-black/25 backdrop-blur-sm
                  transition-opacity duration-300 z-50`}
          onClick={handleClose}
        >
          <div
            className={`${
              isCompactView
                ? "h-full w-full"
                : "h-[90vh] max-h-[90vh] max-w-3xl rounded-lg m-8"
            } relative 
                    bg-light-surface-primary dark:bg-[#2B2E32]
                    shadow-[0_4px_2.6px_0_rgba(0,0,0,0.46),0_3px_14.2px_0_rgba(0,0,0,0.3)]
                    transform transition-all duration-300 ease-in-out
                    overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 z-10
                      text-light-placeholder dark:text-dark-placeholder
                      hover:brightness-150 transition-all duration-300"
            >
              <LuX size={24} />
            </button>

            <div className="h-full flex flex-col overflow-y-auto">
              <div className="p-8 pb-10">
                <Content />
              </div>
            </div>
          </div>
        </div>
      )}
    </FreeTokensModalContext.Provider>
  );
}

function Content() {
  return (
    <div>
      <div className="mb-8 flex-shrink-0">
        <p className="text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
          Free Tokens
        </p>
        <p>Share Darvin with your audience and get rewarded.</p>
      </div>

      <div>
        <p className="font-bold">Get 200,000 Free Tokens</p>
        <p>
          Post about Darvin and include a link to Darvin.dev. After we verify
          your post, we’ll credit your account with 200,000 free tokens.
        </p>
      </div>

      <hr className="my-8 border-white dark:border-[#373B40]" />

      <div>
        <p className="text-2xl font-bold">How it works?</p>
        <ol className="list-decimal list-inside">
          <li>
            <b>Create a post</b> on a social media platform with followers
            (e.g., Twitter/X, LinkedIn, Instagram, TikTok, Facebook).
          </li>
          <li>
            <b>Include a link</b> to{" "}
            <span className="text-primary-blue dark:text-blue-400 underline">
              https://darvin.dev
            </span>{" "}
            in the post.
          </li>
          <li>
            <b>Email us your post link</b> to{" "}
            <a
              href="mailto:free-tokens-reward@darvin.dev?subject=Free%20Tokens%20%E2%80%93%20Post%20Verification"
              className="text-primary-blue dark:text-blue-400 underline"
            >
              free-tokens-reward@darvin.dev
            </a>{" "}
            for verification.
            <ul className="list-disc list-inside ml-4">
              <li>
                Subject: <b>“Free Tokens – Post Verification”</b>
              </li>
              <li>Include in the email:</li>
              <ul className="list-disc list-inside ml-4">
                <li>
                  Your <b>Darvin User ID</b> (find it in Settings → Your
                  Profile)
                </li>
                <li>
                  The <b>direct URL to your social post</b>
                </li>
              </ul>
            </ul>
          </li>
          <li>Once verified, we’ll add the tokens to your account.</li>
        </ol>
      </div>

      <hr className="my-8 border-white dark:border-[#373B40]" />

      <div>
        <p className="text-2xl font-bold">Requirements</p>
        <p>To keep this fair and prevent abuse:</p>
        <ul className="list-disc ml-4">
          <li>
            The social media account must be <b>real and active</b> (not newly
            created for this promo).
          </li>
          <li>
            The account must have a <b>visible follower base</b> (not empty or
            zero-follower).
          </li>
          <li>
            <b>One reward per social account</b> (and per Darvin account).
          </li>
          <li>
            We may decline posts that appear spammy, misleading, or off-topic.
          </li>
        </ul>
      </div>

      <hr className="my-8 border-white dark:border-[#373B40]" />

      <div>
        <p className="text-xl font-bold">Important Note</p>
        <p>
          These free bonus tokens do not carry over to future months. Once your
          current subscription month ends, they will expire and reset to the
          standard monthly tier token amount.
        </p>{" "}
        <p>
          If you receive free tokens and later purchase your first subscription
          without using them, the bonus tokens (just like the free plan tokens)
          will disappear. Your account will simply switch to the token amount
          included in your purchased subscription.
        </p>
      </div>

      <hr className="my-8 border-white dark:border-[#373B40]" />

      <div>
        <p className="font-bold">Submit your post:</p>
        <p>
          <a
            href="mailto:free-tokens-reward@darvin.dev?subject=Free%20Tokens%20%E2%80%93%20Post%20Verification"
            className="text-primary-blue dark:text-blue-400 underline"
          >
            free-tokens-reward@darvin.dev
          </a>
        </p>
      </div>
    </div>
  );
}
